/**
 * Recorded instruments for the score: a small sample player. A manifest (built by packages/lab/loom/samples/build.py)
 * lists, per instrument and articulation ("cello.solo", "strings.pizz", "piano.soft"...), a few recorded notes
 * ("regions") across the range; a note plays the nearest one, re-pitched (playbackRate), in tune (the measured detune),
 * with an attack and a release, looping the held part of sustained notes (the crossfade is baked into the file, so the
 * native loop is seamless), and taking turns between round robins when there are several takes.
 *
 * Sets load whole and lazily (fetch, decodeAudioData, cache) when the score asks for them; a voice only uses a set once
 * every region in it has decoded, so an instrument never mixes recorded and synthesized notes. Decoded audio is large
 * (float32), so the cache keeps the most recently used sets under a byte cap and drops the rest.
 * The audio itself lives wherever `base` points: R2 behind /api/samples/ in production, a local server for renders.
 */

export interface Region { midi: number; file: string; tune?: number; rr?: number; loopStart?: number; loopEnd?: number; len: number; ch: number }
export interface SampleSet { kind: "sustain" | "struck"; regions: Region[]; bytes?: { ogg: number; m4a: number; decoded: number } }
export interface Manifest { version: number; formats: string[]; instruments: Record<string, Record<string, SampleSet>> }

/** "instrument.articulation", as in the manifest. */
export type SetKey = string;

export interface NoteOpts {
  /** Seconds to reach full level (on top of the recording's own attack). */
  attack?: number;
  /** Time constant (s) of the release after `dur`. */
  release?: number;
}

interface Loaded { buffers: Map<string, AudioBuffer>; bytes: number; used: number }

const clamp = (x: number, a: number, b: number) => Math.max(a, Math.min(b, x));

export class Sampler {
  private manifest: Manifest | null = null;
  private loaded = new Map<SetKey, Loaded>();
  private loading = new Map<SetKey, Promise<boolean>>();
  private rr = new Map<string, number>();
  private clock = 0;
  readonly format: "ogg" | "m4a";

  constructor(private opts: { base: string; format?: "ogg" | "m4a"; capBytes?: number; fetch?: typeof fetch }) {
    this.format = opts.format ?? pickFormat();
  }

  /** Fetches the manifest. False (and every voice stays synthesized) if it can't. */
  async init(): Promise<boolean> {
    if (this.manifest) return true;
    try {
      const r = await (this.opts.fetch ?? fetch)(this.url("manifest.json"));
      if (!r.ok) return false;
      this.manifest = (await r.json()) as Manifest;
      return true;
    } catch { return false; }
  }

  private url(path: string) { return this.opts.base.replace(/\/?$/, "/") + path; }
  private set(key: SetKey): SampleSet | undefined {
    const [inst, art] = key.split(".");
    return this.manifest?.instruments[inst!]?.[art!];
  }
  /** The set exists in the manifest. */
  has(key: SetKey): boolean { return !!this.set(key); }
  /** Every region of the set is decoded and ready to play. */
  ready(key: SetKey): boolean { return this.loaded.has(key); }

  /** Loads (or keeps) these sets; drops the least recently used others if the cache is over its cap. */
  async preload(ctx: BaseAudioContext, keys: SetKey[]): Promise<void> {
    const now = ++this.clock;
    for (const k of keys) { const l = this.loaded.get(k); if (l) l.used = now; }
    await Promise.all(keys.filter((k) => this.has(k)).map((k) => this.load(ctx, k)));
    this.evict(new Set(keys));
  }

  load(ctx: BaseAudioContext, key: SetKey): Promise<boolean> {
    if (this.loaded.has(key)) return Promise.resolve(true);
    const pending = this.loading.get(key);
    if (pending) return pending;
    const s = this.set(key);
    if (!s) return Promise.resolve(false);
    const p = (async () => {
      try {
        const buffers = new Map<string, AudioBuffer>();
        let bytes = 0;
        await Promise.all(s.regions.map(async (r) => {
          const res = await (this.opts.fetch ?? fetch)(this.url(`${r.file}.${this.format}`));
          if (!res.ok) throw new Error(`${r.file}: ${res.status}`);
          const buf = await ctx.decodeAudioData(await res.arrayBuffer());
          buffers.set(r.file, buf);
          bytes += buf.length * buf.numberOfChannels * 4;
        }));
        this.loaded.set(key, { buffers, bytes, used: ++this.clock });
        this.evict(new Set([key]));
        return true;
      } catch { return false; } finally { this.loading.delete(key); }
    })();
    this.loading.set(key, p);
    return p;
  }

  /** Bytes of decoded audio held now. */
  get decodedBytes(): number { let n = 0; for (const l of this.loaded.values()) n += l.bytes; return n; }

  private evict(keep: Set<SetKey>): void {
    const cap = this.opts.capBytes ?? 60e6;
    let total = this.decodedBytes;
    const order = [...this.loaded.entries()].filter(([k]) => !keep.has(k)).sort((a, b) => a[1].used - b[1].used);
    for (const [k, l] of order) {
      if (total <= cap) break;
      this.loaded.delete(k); total -= l.bytes;
    }
  }

  /** The region to play for `midi`: the nearest recorded pitch (a recording a little above is preferred: pitched down
   * sounds more natural than up), taking turns between its round robins. */
  private pick(key: SetKey, s: SampleSet, midi: number): Region {
    let best = s.regions[0]!, bestD = Infinity;
    for (const r of s.regions) {
      const d = Math.abs(r.midi - midi) + (r.midi < midi ? 0.1 : 0);
      if (d < bestD) { bestD = d; best = r; }
    }
    const takes = s.regions.filter((r) => r.midi === best.midi);
    if (takes.length < 2) return best;
    const k = `${key}:${best.midi}`;
    const i = this.rr.get(k) ?? 0;
    this.rr.set(k, i + 1);
    return takes[i % takes.length]!;
  }

  /**
   * Plays one note and returns its output (a GainNode, for the caller to route), or null if the set isn't ready (the
   * caller then synthesizes it, and the set starts loading). `dur` (seconds) holds a sustained note, looping it as
   * needed, then releases; null lets a struck note ring out as recorded.
   */
  note(ctx: BaseAudioContext, key: SetKey, at: number, midi: number, vel: number, dur: number | null, o: NoteOpts = {}): GainNode | null {
    const l = this.loaded.get(key);
    const s = this.set(key);
    if (!l || !s) { if (s) void this.load(ctx, key); return null; }
    l.used = ++this.clock;
    const r = this.pick(key, s, midi);
    const buf = l.buffers.get(r.file);
    if (!buf) return null;
    const rate = 2 ** ((midi - r.midi) / 12);
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.playbackRate.value = rate;
    if (r.tune) src.detune.value = r.tune;
    const g = ctx.createGain();
    const attack = Math.max(0.004, o.attack ?? 0.004);
    g.gain.setValueAtTime(0, at);
    g.gain.linearRampToValueAtTime(vel, at + attack);
    const natural = buf.duration / rate;
    src.connect(g);
    src.start(at);
    if (dur !== null) {
      const release = o.release ?? 0.2;
      const looped = r.loopStart !== undefined && r.loopEnd !== undefined && dur > (r.loopEnd - 0.05) / rate;
      if (looped) { src.loop = true; src.loopStart = r.loopStart!; src.loopEnd = r.loopEnd!; }
      const end = at + Math.max(attack, looped ? dur : Math.min(dur, natural));
      g.gain.setValueAtTime(vel, end);
      g.gain.setTargetAtTime(0, end, release);
      src.stop(looped ? end + release * 6 : Math.min(at + natural, end + release * 6));
    } else {
      src.stop(at + natural);
    }
    src.onended = () => { src.disconnect(); g.disconnect(); };
    return g;
  }

  /** What a player downloads (bytes in this format) and holds decoded for these sets. */
  footprint(keys: SetKey[], sampleRate = 48000): { download: number; decoded: number } {
    let download = 0, decoded = 0;
    for (const k of new Set(keys)) {
      const s = this.set(k);
      if (!s) continue;
      download += s.bytes?.[this.format] ?? 0;
      decoded += s.regions.reduce((n, r) => n + Math.ceil(r.len * sampleRate) * r.ch * 4, 0);
    }
    return { download, decoded };
  }
}

/** Opus where the browser can decode it, else AAC (Safari before Opus support). */
function pickFormat(): "ogg" | "m4a" {
  try {
    if (typeof Audio === "undefined") return "ogg";
    return new Audio().canPlayType('audio/ogg; codecs="opus"') ? "ogg" : "m4a";
  } catch { return "ogg"; }
}

export const _test = { clamp };
